import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { chClientReviewService, mergeReport, type ClientReviewData } from '../services/clientService';
import { ClientReviewContent } from '../components/ClientReviewContent';

const NAME_STORAGE_KEY = 'ch-review-name';

export function MagicLinkReview() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ClientReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actorName, setActorName] = useState(() => localStorage.getItem(NAME_STORAGE_KEY) || '');
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [nameInput, setNameInput] = useState('');

  useEffect(() => {
    if (!token) return;
    loadData();
  }, [token]);

  async function loadData() {
    try {
      setLoading(true);
      const result = await chClientReviewService.validateToken(token!);
      if (!result.valid) {
        setError(result.error || 'Token inválido');
      } else {
        // Stats, prints and published links (empty until the stats SQL is applied)
        const report = await chClientReviewService.getReport(token!);
        setData(mergeReport(result, report));
      }
    } catch {
      setError('Erro ao validar o link');
    } finally {
      setLoading(false);
    }
  }

  function handleNameSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!nameInput.trim()) return;
    const name = nameInput.trim();
    setActorName(name);
    localStorage.setItem(NAME_STORAGE_KEY, name);
    setShowNameDialog(false);
  }

  function handleNeedName() {
    setShowNameDialog(true);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">A carregar...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">!</span>
          </div>
          <h1 className="text-lg font-bold text-gray-900 mb-2">Link indisponível</h1>
          <p className="text-sm text-gray-500 mb-6">
            {/revogad|invalid|inválid/i.test(error)
              ? 'Este link já não está ativo. Pede um novo link à equipa MOHIT.'
              : /expir/i.test(error)
                ? 'Este link expirou. Pede um novo link à equipa MOHIT.'
                : error}
          </p>
          <p className="text-[10px] uppercase tracking-widest text-gray-300 font-medium">
            Powered by MOHIT
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <>
      <ClientReviewContent
        data={data}
        token={token!}
        actorName={actorName}
        onNeedName={handleNeedName}
        viewOnly={data.mode === 'view'}
      />

      {/* Name dialog */}
      {showNameDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <form
            onSubmit={handleNameSubmit}
            className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-bold text-gray-900 mb-1">Como te chamas?</h3>
            <p className="text-sm text-gray-500 mb-4">Para registo da aprovação.</p>
            <input
              type="text"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
              placeholder="O teu nome"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              autoFocus
            />
            <button
              type="submit"
              disabled={!nameInput.trim()}
              className="w-full mt-4 px-4 py-2.5 rounded-full bg-gray-900 text-white font-bold text-sm disabled:opacity-50 transition-transform hover:scale-[1.02]"
            >
              Continuar
            </button>
          </form>
        </div>
      )}
    </>
  );
}
